export type TransferDiscountSettings = {
  enabled?: unknown
  discountActive?: unknown
  discountPercent?: unknown
}

export type TransferDiscountLine = {
  price?: unknown
  quantity?: unknown
  disableTransferDiscount?: unknown
  isSale?: unknown
}

export type TransferUnitPricingResult = {
  showTransferPrice: boolean
  transferPrice: number
  transferPercent: number
  transferDiscountAmount: number
}

export type CheckoutTransferDiscountResult = {
  eligibleTransferSubtotal: number
  paymentMethodDiscount: number
  transferPercent: number
}

const toFiniteNumber = (value: unknown): number | null => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number(value)
        : Number.NaN
  return Number.isFinite(parsed) ? parsed : null
}

export const roundCurrencyAmount = (value: number): number =>
  Math.round((Number(value) || 0) * 100) / 100

export const resolveActiveTransferPercent = (
  settings?: TransferDiscountSettings | null
): number => {
  const percent = Math.min(
    100,
    Math.max(0, toFiniteNumber(settings?.discountPercent) ?? 0)
  )
  return settings?.enabled === true &&
    settings?.discountActive === true &&
    percent > 0
    ? percent
    : 0
}

export const isTransferDiscountEligible = (
  line?: Pick<TransferDiscountLine, "disableTransferDiscount"> | null
): boolean => line?.disableTransferDiscount !== true

export const resolveTransferUnitPricing = ({
  unitPrice,
  disableTransferDiscount,
  settings,
}: {
  unitPrice: unknown
  disableTransferDiscount?: unknown
  settings?: TransferDiscountSettings | null
}): TransferUnitPricingResult => {
  const price = Math.max(0, toFiniteNumber(unitPrice) ?? 0)
  const transferPercent = resolveActiveTransferPercent(settings)
  const showTransferPrice =
    price > 0 &&
    transferPercent > 0 &&
    disableTransferDiscount !== true
  const transferDiscountAmount = showTransferPrice
    ? roundCurrencyAmount((price * transferPercent) / 100)
    : 0

  return {
    showTransferPrice,
    transferPrice: showTransferPrice
      ? roundCurrencyAmount(Math.max(0, price - transferDiscountAmount))
      : price,
    transferPercent: showTransferPrice ? transferPercent : 0,
    transferDiscountAmount,
  }
}

export const resolveCheckoutTransferDiscount = ({
  items,
  paymentMethod,
  settings,
}: {
  items: TransferDiscountLine[]
  paymentMethod: string
  settings?: TransferDiscountSettings | null
}): CheckoutTransferDiscountResult => {
  const eligibleTransferSubtotal = roundCurrencyAmount(
    (Array.isArray(items) ? items : []).reduce((sum, item) => {
      if (!isTransferDiscountEligible(item)) return sum
      const price = Math.max(0, toFiniteNumber(item?.price) ?? 0)
      const quantity = Math.max(0, toFiniteNumber(item?.quantity) ?? 0)
      return sum + price * quantity
    }, 0)
  )
  const activePercent = resolveActiveTransferPercent(settings)
  const transferPercent = paymentMethod === "wire" ? activePercent : 0
  const paymentMethodDiscount =
    transferPercent > 0
      ? Math.min(
          eligibleTransferSubtotal,
          roundCurrencyAmount(
            (eligibleTransferSubtotal * transferPercent) / 100
          )
        )
      : 0

  return {
    eligibleTransferSubtotal,
    paymentMethodDiscount,
    transferPercent:
      paymentMethodDiscount > 0 ? transferPercent : 0,
  }
}

export const resolveCheckoutTransferLinePricing = ({
  item,
  paymentMethod,
  settings,
}: {
  item: TransferDiscountLine
  paymentMethod: string
  settings?: TransferDiscountSettings | null
}) => {
  const lineTotal = roundCurrencyAmount(
    Math.max(0, toFiniteNumber(item?.price) ?? 0) *
      Math.max(0, toFiniteNumber(item?.quantity) ?? 0)
  )
  const unitPricing = resolveTransferUnitPricing({
    unitPrice: lineTotal,
    disableTransferDiscount: item?.disableTransferDiscount,
    settings: paymentMethod === "wire" ? settings : null,
  })

  return {
    lineTotal,
    finalLineTotal: unitPricing.transferPrice,
    discountAmount: unitPricing.transferDiscountAmount,
    transferPercent: unitPricing.transferPercent,
    hasTransferDiscount: unitPricing.showTransferPrice,
  }
}
